import {
  Check,
  ChevronLeft,
  ChevronRight,
  ImagePlus,
  RotateCw,
  Sparkles,
} from "lucide-react";
import type { useWorkspace } from "../hooks/useWorkspace";
import {
  interpolate,
  localizeDirection,
  localizePurposeForLocale,
  localizeRationale,
  t,
} from "../i18n";
import "./ImageReview.css";

type Workspace = ReturnType<typeof useWorkspace>;

export function ImageReview({ workspace }: { workspace: Workspace }) {
  const { state, busy, notice, progress, history } = workspace;
  const selectedPlan =
    state.plan.find((slot) => slot.id === state.selectedSlotId) ??
    state.activeSlots[0];
  const selectedImage = state.images.find(
    (image) => image.slotId === selectedPlan?.id,
  );
  const activeIndex = state.images.findIndex(
    (image) => image.slotId === selectedPlan?.id,
  );
  const selectRelative = (offset: number) => {
    const target = state.images[activeIndex + offset];
    if (target) workspace.selectSlot(target.slotId);
  };

  const batchStatusKeys = {
    complete: "batchComplete",
    failed: "batchFailed",
    "in-progress": "batchInProgress",
  } as const;

  const quickChanges = [
    t(state.locale, "changeBackground"),
    t(state.locale, "adjustLighting"),
    t(state.locale, "changeCopy"),
    t(state.locale, "changeComposition"),
  ];
  if (!selectedPlan || !selectedImage)
    return (
      <section
        className="review-canvas empty-review"
        aria-labelledby="review-title"
      >
        <p className="eyebrow">03 · {t(state.locale, "review")}</p>
        <h1 id="review-title">{t(state.locale, "reviewTitle")}</h1>
        <p className="empty-review-copy">
          {t(
            state.locale,
            history.length ? "chooseSavedSession" : "noSavedSessions",
          )}
        </p>
        {history.length > 0 ? (
          <div className="batch-history saved-sessions">
            <h2>{t(state.locale, "savedSessions")}</h2>
            {history.slice(0, 10).map((batch) => (
              <button
                key={batch.id}
                type="button"
                onClick={() => workspace.loadHistory(batch)}
              >
                <span>
                  {new Date(batch.timestamp).toLocaleString(state.locale)}
                </span>
                <small>{t(state.locale, batchStatusKeys[batch.status])}</small>
              </button>
            ))}
          </div>
        ) : (
          <div className="empty-review-card">
            {t(state.locale, "generatedSessionsHint")}
          </div>
        )}
      </section>
    );

  const protectionInstructions = [
    selectedPlan.preserve.shape ? t(state.locale, "productShape") : null,
    selectedPlan.preserve.logo ? t(state.locale, "brandMark") : null,
    selectedPlan.preserve.colours ? t(state.locale, "productColour") : null,
    selectedPlan.preserve.packaging ? t(state.locale, "packagingText") : null,
  ].filter((instruction): instruction is string => Boolean(instruction));

  return (
    <section className="review-canvas" aria-labelledby="review-title">
      {notice && (
        <div className="status-chip" role="status">
          {notice}
        </div>
      )}
      {busy && (
        <div className="generation-progress" role="status" aria-live="polite">
          {t(state.locale, "generating")}… {progress}%
        </div>
      )}
      <div className="review-heading">
        <div>
          <p className="eyebrow">03 · {t(state.locale, "review")}</p>
          <h1 id="review-title">{t(state.locale, "reviewTitle")}</h1>
          <p>
            {interpolate(t(state.locale, "reviewProgress"), {
              approved: state.images.filter((image) => image.approved).length,
              total: state.images.length,
            })}
          </p>
        </div>
        <div className="review-actions">
          <button
            type="button"
            className="outline-action"
            onClick={workspace.exportCurrent}
          >
            {t(state.locale, "exportImages")}
          </button>
          <button type="button" className="outline-action">
            <ImagePlus size={16} /> {t(state.locale, "addImage")}
          </button>
        </div>
      </div>
      <div className="review-layout">
        <div className="review-main">
          <div className={`hero-image palette-${selectedImage.palette}`}>
            <div className="image-overlay">
              <span>{String(activeIndex + 1).padStart(2, "0")}</span>
              <em>{localizeDirection(state.locale, selectedPlan.direction)}</em>
            </div>
            {selectedImage.imageUrl ? (
              <img
                src={selectedImage.imageUrl}
                alt={localizePurposeForLocale(
                  state.locale,
                  selectedPlan.purpose,
                )}
              />
            ) : selectedImage.error ? (
              <div role="alert" className="generation-error">
                {selectedImage.error}
              </div>
            ) : (
              <div className="pending-preview">
                {(state.productImageUrl || state.referenceImages[0]?.data) && (
                  <img
                    src={state.productImageUrl || state.referenceImages[0].data}
                    alt=""
                  />
                )}
                <span className="loading-spinner" aria-hidden="true" />
                <strong>{t(state.locale, "generating")}…</strong>
              </div>
            )}
          </div>
          <div className="hero-caption">
            <div>
              <h2>
                {localizePurposeForLocale(state.locale, selectedPlan.purpose)}
              </h2>
              <p>{localizeRationale(state.locale, selectedPlan.rationale)}</p>
            </div>
            <span
              className={
                selectedImage.approved ? "status-chip approved" : "status-chip"
              }
            >
              {selectedImage.approved
                ? t(state.locale, "approved")
                : t(state.locale, "pending")}
            </span>
          </div>
          <div className="review-controls">
            <button
              type="button"
              aria-label={t(state.locale, "previousImage")}
              disabled={activeIndex === 0}
              onClick={() => selectRelative(-1)}
            >
              <ChevronLeft size={18} /> {t(state.locale, "previousImage")}
            </button>
            <button
              type="button"
              aria-label={t(state.locale, "nextImage")}
              disabled={activeIndex === state.images.length - 1}
              onClick={() => selectRelative(1)}
            >
              {t(state.locale, "nextImage")} <ChevronRight size={18} />
            </button>
          </div>
          <div
            className="image-strip"
            aria-label={t(state.locale, "allImages")}
          >
            {state.images.map((image, index) => {
              const plan = state.plan.find((slot) => slot.id === image.slotId)!;
              const waiting = !image.imageUrl && !image.error;
              return (
                <button
                  key={image.slotId}
                  type="button"
                  className={`thumbnail ${image.slotId === selectedPlan.id ? "active" : ""} status-${image.approved ? "approved" : image.status === "failed" ? "failed" : waiting ? "loading" : "pending"}`}
                  onClick={() => workspace.selectSlot(image.slotId)}
                  aria-label={`${t(state.locale, "selectImage")}: ${localizePurposeForLocale(state.locale, plan.purpose)}`}
                  title={`${String(index + 1).padStart(2, "0")} · ${image.approved ? t(state.locale, "approved") : waiting ? t(state.locale, "generating") : t(state.locale, "pending")}`}
                >
                  <span className={`thumb-art palette-${image.palette}`}>
                    {image.imageUrl ? (
                      <img src={image.imageUrl} alt="" />
                    ) : image.error ? (
                      <span className="thumb-error">!</span>
                    ) : (
                      <>
                        <span className="loading-spinner" aria-hidden="true" />
                        {(state.productImageUrl ||
                          state.referenceImages[0]?.data) && (
                          <img
                            src={
                              state.productImageUrl ||
                              state.referenceImages[0].data
                            }
                            alt=""
                          />
                        )}
                      </>
                    )}
                  </span>
                  <small>
                    <b>{String(index + 1).padStart(2, "0")}</b>
                    <i aria-hidden="true" />
                  </small>
                </button>
              );
            })}
          </div>
        </div>
        <aside className="review-inspector">
          <div className="inspector-top">
            <span>
              <Sparkles size={15} />
              {t(state.locale, "onlyThisImage")}
            </span>
            <strong>
              {selectedImage.status === "generating"
                ? `${t(state.locale, "generating")}…`
                : `${t(state.locale, "version")} ${selectedImage.version}`}
            </strong>
          </div>
          <h2>{t(state.locale, "quickAdjustments")}</h2>
          <div className="quick-changes">
            {quickChanges.map((change) => (
              <button
                key={change}
                type="button"
                className={
                  selectedImage.quickAdjustments.includes(change)
                    ? "selected"
                    : ""
                }
                onClick={() => workspace.addQuickAdjustment(change)}
              >
                {change}
              </button>
            ))}
          </div>
          <label htmlFor="change-request">
            {t(state.locale, "changeRequest")}
            <textarea
              id="change-request"
              value={selectedImage.request}
              onChange={(event) =>
                workspace.updateSelectedRequest(event.target.value)
              }
            />
          </label>
          <button
            className={
              selectedImage.approved
                ? "approve-action is-approved"
                : "approve-action primary-action"
            }
            disabled={selectedImage.status === "generating"}
            type="button"
            onClick={workspace.approveSelected}
          >
            <Check size={16} />
            {selectedImage.approved
              ? t(state.locale, "approved")
              : t(state.locale, "approve")}
          </button>
          <button
            className="regenerate secondary-action"
            type="button"
            disabled={selectedImage.status === "generating"}
            onClick={workspace.regenerateSelected}
          >
            <RotateCw size={16} />
            {selectedImage.status === "generating"
              ? `${t(state.locale, "generating")}…`
              : t(state.locale, "regenerate")}
          </button>
          <fieldset className="integrity-instructions">
            <legend>{t(state.locale, "generationProtection")}</legend>
            <div className="integrity-instruction-list">
              {protectionInstructions.map((instruction) => (
                <span key={instruction}>{instruction}</span>
              ))}
            </div>
          </fieldset>
          <p className="inspector-note">{t(state.locale, "protectionNote")}</p>
        </aside>
      </div>
      {history.length > 0 && (
        <details className="batch-history">
          <summary>
            {interpolate(t(state.locale, "savedBatches"), {
              count: history.length,
            })}
          </summary>
          {history.slice(0, 5).map((batch) => (
            <button
              key={batch.id}
              type="button"
              onClick={() => workspace.loadHistory(batch)}
            >
              {new Date(batch.timestamp).toLocaleString(state.locale)} ·{" "}
              {t(state.locale, batchStatusKeys[batch.status])}
            </button>
          ))}
        </details>
      )}
    </section>
  );
}
